import {DateTime} from 'luxon';
import React, {FC, ReactNode, ReactText, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {newPalette, NewPaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import csatUpsellCardImage from '../../../../assets/images/analyticsCsatUpsellCard.svg';
import {ContactSalesButton} from '../../../../components/common/callToAction/contactSalesButton';
import {LearnMoreButton} from '../../../../components/common/callToAction/learnMoreButton';
import {UpsellCard} from '../../../../components/common/upsellCard/upsellCard';
import {Pill} from '../../../../components/pill/pill';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsDonutMetricValue,
  AnalyticsGraphMetricsEnum,
  AnalyticsGraphMetricValue,
  AnalyticsMetricError,
  AnalyticsScalarMetric,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetric,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getAnalyticsPreviousPeriodDatetimes} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {makeGetAnalyticsMetric} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {helpCenterUrl} from '../../../../store/stateless/navigationActions';
import {
  getMetricRenderer,
  maybeGetPreviousPeriodFormatter,
  renderFormattedMetricData,
} from '../../../helpers/analyticsFormatHelpers';
import {
  BasicReportProps,
  ContributingSegmentsOptionsEnum,
  isComputedGraphMetric,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {AnalyticsComputedGraphMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {ChartSerie} from '../../charts/commons/types';
import {CsatExportDialog} from '../../exports/csatExportDialog';
import {
  computeMetricErrorTitle,
  ErrorStateRenderer,
  MetricErrorState,
} from '../../reportElements/charts/common/metricErrorState';
import {EmptyStateRenderer} from '../../reportElements/charts/emptyState';
import {NoRuleEmptyState} from '../../reportElements/charts/noRuleEmptyState';
import {ReportDonutChart} from '../../reportElements/charts/reportDonutChart';
import {ReportLineChart} from '../../reportElements/charts/reportLineChart';
import {AnyGraphMetricsEnum} from '../../reportElements/helperTypes';
import {ReportList} from '../../reportElements/list/reportList';
import {ReportElementWrapper} from '../../reportElements/reportElement';
import {SurveySampleFeedbackTable} from '../../reportElements/surveySampleFeedbackTable/surveySampleFeedbackTable';
import {CustomerReportTable} from './customerReportTable';

/*
 * Constants.
 */

const perfGraphSeries: Array<ChartSerie<AnalyticsGraphMetricValue>> = [
  {
    accessor: (d) => d.value,
    label: getMetricLabels(AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH).label,
    color: newPalette[NewPaletteColorsEnum.GREEN].regular,
  },
];

enum CustomerExperienceLabelAccessorsEnum {
  CSAT_SURVEY_SCORE = 'csat_survey_score',
}

// Tooltips
const sampleFeedbackTooltip = {
  title: (
    <Trans id="analytics-customer-experience-report-survey-sample-feedback-table-tooltip-title">
      Survey comments
    </Trans>
  ),
  tooltipContent: (
    <Trans id="analytics-customer-experience-report-survey-sample-feedback-table-tooltip">
      These are examples of written feedback received through your survey. Click the export icon to download a
      complete list.
    </Trans>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/60hz4mv/customer-experience-report`,
};

const satisfactionScoreTooltip = {
  title: <Trans id="analytics-customer-experience-report-scores-title">Satisfaction score</Trans>,
  tooltipContent: (
    <Trans id="analytics-customer-experience-report-scores-tooltip">
      Percent of survey responses this period with 4 or 5 stars
    </Trans>
  ),
  learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd `,
};

/*
 * Styles.
 */

const DonutWrapper = styled.div`
  width: 50%;
`;

const ListWrapper = styled.div`
  width: 30%;
`;

const ScoresReportWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const TopRowWrapper = styled.div`
  display: flex;
  grid-column: span 12;
`;

/* The donut-chart and its corresponding report list. */
const TopRowLeftWrapper = styled.div`
  flex: 1;
`;

const TopRowLeftEmptyStateWrapper = styled.div`
  margin-top: 62px;
  margin-left: 50%;
`;

/* The sample feedback table. */
const TopRowRightWrapper = styled.div`
  flex: 0 30%;
  margin-left: 48px;
`;

const CsatUpsellCardWrapper = styled.div`
  grid-column: span 12;
`;

/*
 * Props.
 */

interface CustomerExperienceReportProps extends BasicReportProps {
  hasAccountsAnalyticsFeature: boolean;
  hasCsatSurveysFeature: boolean;
}

/*
 * Component.
 */

export const CustomerExperienceReport: FC<CustomerExperienceReportProps> = ({
  isLoading,
  hasAccountsAnalyticsFeature,
  hasCsatSurveysFeature,
}) => {
  const [previousPeriodStart, previousPeriodEnd] = useAppSelector(getAnalyticsPreviousPeriodDatetimes);
  const [shouldShowHistoExportDialog, toggleHistoExportDialog] = useToggle();
  const [shouldShowSampleFeedbackExportDialog, toggleSampleFeedbackExportDialog] = useToggle();

  const [totalSurveyCountData, totalSurveyCountError] = useAppSelector(
    makeGetAnalyticsMetric(AnalyticsScalarMetricsEnum.NUM_CSAT_SURVEY_RESPONSE),
  );

  const [surveySampleMetricData] = useAppSelector(
    makeGetAnalyticsMetric(AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE),
  );

  const [hoveredItemId, setHoveredItemId] = useState<string>();
  const dispatch = useAppDispatch();

  const onDataPointSelect = (
    dataPoint: AnalyticsGraphMetricValue,
    serie: ChartSerie<AnalyticsGraphMetricValue>,
    selectedMetric?: AnyGraphMetricsEnum,
  ) => {
    // Adding a type guard but it shouldn't technically happen.
    if (!selectedMetric || isComputedGraphMetric(selectedMetric)) {
      return;
    }

    dispatch(
      setMetricDetailsAction(selectedMetric, {
        [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
          [ContributingSegmentsOptionsEnum.ROW]: dataPoint.start,
        },
      }),
    );
  };

  const onCsatSurveyResponseScoreClick = ({label}: {label: ReactNode}, index: number) => {
    dispatch(
      setMetricDetailsAction(
        AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: 'survey_score',
            [ContributingSegmentsOptionsEnum.ROW]: index,
          },
        },
        `CSAT Survey Responses: ${label}`,
      ),
    );
  };

  const onDonutArcClick = (index: number, item?: AnalyticsDonutMetricValue) => {
    if (item) {
      onCsatSurveyResponseScoreClick(item, index);
    }
  };

  // CSAT content or a CSAT Upsell Card.
  const surveyContent = (
    <>
      <TopRowWrapper>
        <TopRowLeftWrapper>
          {/* Customer Satisfaction scores */}
          <ReportElementWrapper
            title={
              <Trans id="analytics-customer-experience-report-scores-donut-title">
                Customer Satisfaction
              </Trans>
            }
            subtitle={renderCSATSubtitle(
              totalSurveyCountData,
              totalSurveyCountError,
              previousPeriodStart,
              previousPeriodEnd,
            )}
            infoTooltip={satisfactionScoreTooltip}
            reportSectionId={ReportElementIdsEnum.CX_SCORES_DONUT_WRAPPER}
            width={12}
            isExportable
            toggleExportDialog={toggleHistoExportDialog}
          >
            {/* Customer Satisfaction Scores Donut */}
            <ScoresReportWrapper>
              <DonutWrapper>
                <ReportDonutChart
                  reportSectionId={ReportElementIdsEnum.CX_SCORES_DONUT}
                  height={220}
                  isLoading={isLoading}
                  metric={AnalyticsComputedGraphMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO_GRAPH}
                  accessor={(d: AnalyticsGraphMetricValue) => d.value}
                  reportListBindId={CustomerExperienceLabelAccessorsEnum.CSAT_SURVEY_SCORE}
                  hoveredItemId={hoveredItemId}
                  onArcClick={onDonutArcClick}
                  onArcHover={(index?: number) =>
                    setHoveredItemId(
                      makeHoveredItemId(CustomerExperienceLabelAccessorsEnum.CSAT_SURVEY_SCORE, index),
                    )
                  }
                  innerMetric={AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION}
                  innerMetricType={AnalyticsDataPointTypesEnum.PERCENTAGE}
                  renderEmptyState={topRowLeftEmptyStateRenderer}
                  renderErrorState={topRowLeftErrorStateRenderer}
                />
              </DonutWrapper>
              {/* Customer Satisfaction Scores List */}
              <ListWrapper>
                <ReportList
                  reportSectionId={ReportElementIdsEnum.CX_SCORES_LIST_WRAPPER}
                  isLoading={isLoading}
                  metric={AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO}
                  labelAccessor={CustomerExperienceLabelAccessorsEnum.CSAT_SURVEY_SCORE}
                  valueAccessors={['csat_survey_responses']}
                  hoveredItemId={hoveredItemId}
                  labelCircleColor={PaletteColorsEnum.BLUE}
                  onItemClick={onCsatSurveyResponseScoreClick}
                  onItemHover={(index?: ReactText) =>
                    setHoveredItemId(
                      makeHoveredItemId(CustomerExperienceLabelAccessorsEnum.CSAT_SURVEY_SCORE, index),
                    )
                  }
                  shouldShowExportDialog={shouldShowHistoExportDialog}
                  toggleExportDialog={toggleHistoExportDialog}
                  // No need for an empty or error state since there is one in the donut.
                  renderEmptyState={() => null}
                  renderErrorState={() => null}
                />
              </ListWrapper>
            </ScoresReportWrapper>
          </ReportElementWrapper>
        </TopRowLeftWrapper>

        <TopRowRightWrapper>
          {/* Customer Satisfaction scores */}
          <ReportElementWrapper
            title={
              <Trans id="customer-experience-report-survey-sample-feedback-table">Survey comments</Trans>
            }
            subtitle={renderSampleFeedbackSubTitle(
              totalSurveyCountData,
              totalSurveyCountError,
              surveySampleMetricData,
            )}
            infoTooltip={sampleFeedbackTooltip}
            reportSectionId={ReportElementIdsEnum.CX_SAMPLE_FEEDBACK_TABLE}
            width={12}
            toggleExportDialog={toggleSampleFeedbackExportDialog}
            isExportable
          >
            {/* Sample feedback table */}
            <SurveySampleFeedbackTable
              reportSectionId={ReportElementIdsEnum.CX_SAMPLE_FEEDBACK_TABLE}
              isLoading={isLoading}
              metric={AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE}
              renderEmptyState={csatEmptyStateRenderer}
            />

            {/* Export dialog popover for sample feedback -- this one's async */}
            {shouldShowSampleFeedbackExportDialog && (
              <CsatExportDialog onRequestClose={toggleSampleFeedbackExportDialog} />
            )}
          </ReportElementWrapper>
        </TopRowRightWrapper>
      </TopRowWrapper>

      {/* Customer Satisfaction Performance Over Time Chart */}
      <ReportLineChart
        reportSectionId={ReportElementIdsEnum.CX_CHANGES_OVER_TIME_CHART}
        title={<Trans id="analytics-csat-survey-report-changes-title">Changes Over Time</Trans>}
        isLoading={isLoading}
        availableMetrics={[
          AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH,
          AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH,
        ]}
        series={perfGraphSeries}
        xAccessor={(d: AnalyticsGraphMetricValue) => d.label}
        onDataPointSelect={onDataPointSelect}
        renderEmptyState={csatEmptyStateRenderer}
      />
    </>
  );

  return (
    <>
      {hasCsatSurveysFeature ? surveyContent : renderUpsellCard()}

      {/* Customer experience table */}
      <CustomerReportTable
        isLoading={isLoading}
        hasAccountsAnalyticsFeature={hasAccountsAnalyticsFeature}
        hasCsatSurveysFeature={hasCsatSurveysFeature}
      />
    </>
  );
};

/*
 * Helpers.

 */
function makeHoveredItemId(listId: CustomerExperienceLabelAccessorsEnum, itemIndex?: ReactText) {
  return `${listId}-${itemIndex}`;
}

function renderUpsellCard() {
  return (
    <CsatUpsellCardWrapper>
      <UpsellCard
        title={<Trans id="csat-analytics-report-upsell-title">Track customer satisfaction ratings</Trans>}
        pill={
          <Pill size={VisualSizesEnum.MEDIUM}>
            <Trans id="analytics-csat-upsell-available-with-growth">AVAILABLE WITH GROWTH</Trans>
          </Pill>
        }
        imageUrl={csatUpsellCardImage}
        buttons={[
          <ContactSalesButton />,
          <LearnMoreButton helpPageUrl={`${helpCenterUrl}/t/p8hf16d/fronts-csat-feature`} />,
        ]}
        limitContentWidth={false}
      >
        <Trans id="csat-analytics-report-upsell-body">
          Measure and improve customer satisfaction with CSAT surveys.
        </Trans>
      </UpsellCard>
    </CsatUpsellCardWrapper>
  );
}

const topRowLeftEmptyStateRenderer: EmptyStateRenderer = ({width, height}) => (
  <TopRowLeftEmptyStateWrapper>{csatEmptyStateRenderer({width, height})}</TopRowLeftEmptyStateWrapper>
);

const topRowLeftErrorStateRenderer: ErrorStateRenderer = ({metricError, width, height}) => (
  <TopRowLeftEmptyStateWrapper>
    <MetricErrorState error={metricError} height={height} width={width} />
  </TopRowLeftEmptyStateWrapper>
);

const csatEmptyStateRenderer: EmptyStateRenderer = ({width, height}) => (
  <NoRuleEmptyState
    width={width}
    height={height}
    title={<Trans id="analytics-empty-state-csat-title">CSAT not set up</Trans>}
    buttonText={<Trans id="analytics-csat-empty-state-no-rule-button">Set up CSAT</Trans>}
    learnMoreLink={`${helpCenterUrl}/t/p8hf16d/fronts-csat-feature`}
    interactionId="csat-analytics-empty-set-up-rule"
  />
);

const renderTotalCountError = (totalCountError: AnalyticsMetricError) => {
  const {title, subtitle} = computeMetricErrorTitle(totalCountError);
  return (
    <>
      <Trans id="analytics-customer-experience-report-total-count-sub-title-error">
        Error computing the count of surveys in this period:
      </Trans>{' '}
      {title}. {subtitle}
    </>
  );
};

const renderCSATSubtitle = (
  totalSurveyCountData?: AnalyticsScalarMetric,
  totalCountError?: AnalyticsMetricError,
  previousPeriodStart?: DateTime,
  previousPeriodEnd?: DateTime,
) => {
  if (totalCountError) {
    return renderTotalCountError(totalCountError);
  }

  return (
    <Trans id="analytics-customer-experience-report-scores-donut-sub-title-1">
      Scores based on {maybeDisplayTotalSurveyData(totalSurveyCountData)} survey responses this period.
      Previous period: {maybeDisplayPreviousTotalSurveyData(totalSurveyCountData)} responses,{' '}
      {maybeGetPreviousPeriodFormatter(previousPeriodStart, previousPeriodEnd)}.
    </Trans>
  );
};

const renderSampleFeedbackSubTitle = (
  totalSurveyCountData?: AnalyticsScalarMetric,
  totalCountError?: AnalyticsMetricError,
  surveySampleMetricData?: AnalyticsTableMetric,
) => {
  if (totalCountError) {
    return renderTotalCountError(totalCountError);
  }

  return (
    <>
      {maybeDisplaySurveyDataCount(totalSurveyCountData, surveySampleMetricData)}
      <Trans id="analytics-customer-experience-report-survey-sample-feedback-table-sub-title">
        Export to see all, including comments.
      </Trans>
    </>
  );
};

const maybeDisplayTotalSurveyData = (totalCountMetricData?: AnalyticsScalarMetric) => {
  if (!totalCountMetricData) {
    return '-';
  }

  return renderFormattedMetricData(totalCountMetricData, AnalyticsDataPointTypesEnum.NUMERICAL);
};

const maybeDisplaySurveyDataCount = (
  totalCountMetricData?: AnalyticsScalarMetric,
  surveySampleMetricData?: AnalyticsTableMetric,
) => {
  if (!totalCountMetricData || !surveySampleMetricData) {
    return null;
  }

  const totalCountMetricValue = totalCountMetricData.value;
  const sampleCountDisplayValue = Math.min(3, surveySampleMetricData.rows.length);

  if (sampleCountDisplayValue < 3 && sampleCountDisplayValue === totalCountMetricValue) {
    return null;
  } // Do not display 0 of 0, 1 of 1 or 2 of 2.

  const formattedMetricData = renderFormattedMetricData(
    totalCountMetricData,
    AnalyticsDataPointTypesEnum.NUMERICAL,
  );

  return (
    <>
      <Trans id="analytics-customer-experience-report-survey-sample-feedback-table-count">
        {sampleCountDisplayValue} of {formattedMetricData}.
      </Trans>{' '}
    </>
  );
};

const maybeDisplayPreviousTotalSurveyData = (totalCountMetricData?: AnalyticsScalarMetric) => {
  if (!totalCountMetricData) {
    return '-';
  }

  return getMetricRenderer(AnalyticsDataPointTypesEnum.NUMERICAL)({
    value: totalCountMetricData.previousValue,
  });
};
